import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { getOverviewDetails } from '../../../../../../utils/api_service/SmartInterview';
import { UpcommingInterviews } from './Components';
import PieChart from './Components/Chart';

interface ChartItem {
  label: string;
  value: number;
}

interface UpcomingInterview {
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  interviewStartTime: number;
}

interface OverviewData {
  interviewTypeChart: ChartItem[];
  interviewApplicantStatusChart: ChartItem[];
  statusChart: ChartItem[];
  upcomingInterview: UpcomingInterview[];
}

interface OverviewProps {
  interviewRoundId?: string;
}

export const Overview: React.FC<OverviewProps> = ({ interviewRoundId }) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);

  const fetchOverviewDetails = async () => {
    if (!interviewRoundId) return;
    try {
      const response = await getOverviewDetails(interviewRoundId);
      const data = await response.json();
      setOverviewData(data);
    } catch {
      setOverviewData(null);
    }
  };

  useEffect(() => {
    fetchOverviewDetails();
  }, [interviewRoundId]);

  if (!overviewData) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: 180,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          fontSize: '14px',
        }}
      >
        {t(SI.loading.overview)}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', minWidth: 0, height: '100%' }}>
            <PieChart
              data={overviewData.interviewApplicantStatusChart}
              title={t(SI.overview.interviewApplicantStatus)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', minWidth: 0, height: '100%' }}>
            <PieChart
              data={overviewData.interviewTypeChart}
              title={t(SI.overview.interviewTypes)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', minWidth: 0, height: '100%' }}>
            <PieChart
              data={overviewData.statusChart}
              title={t(SI.overview.interviewStatus)}
            />
          </Box>
        </Grid>
      </Grid>

      <UpcommingInterviews interviews={overviewData.upcomingInterview} />
    </Box>
  );
};

export default Overview;
